import React, { useState } from 'react';
import { formatDisplayDate, MenuIconColor, ModalWidth, ROW_GUTTER } from '@/config';
import { Button, Card, Col, Divider, message, Modal, Row, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { SubscriptionIcon } from '@/assets/icons/entities/_';
import moment from 'moment';
import { PlanSubscribeViewModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { formatCurrency } from '@/helpers/format';

type Props = {
  planId: string;
  onClose: (reload: boolean) => void;
};

export default function SubscriptionModal({ onClose, planId }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [planSubscribe, setPlanSubscribe] = React.useState<PlanSubscribeViewModel>();
  React.useEffect(() => {
    if (planId) {
      setLoading(true);
      apiSdk.PlanService.getSubscribeViewModel({
        id: planId,
      })
        .then(res => {
          setPlanSubscribe(res);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [planId]);
  const subscribeNow = () => {
    setLoading(true);
    apiSdk.PlanService.subscribe({
      id: planId,
    })
      .then(response => {
        if (response?.success) {
          onClose(true);
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Modal
      visible={true}
      maskClosable={false}
      width={ModalWidth.medium}
      className="nta-custom-form"
      title={t('subscriptionsPage_Subscribe')}
      forceRender={true}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={() => {
        onClose(false);
      }}
    >
      <Spin spinning={loading}>
        <h3 style={{ color: MenuIconColor.AdminPortal, fontWeight: 'bold' }}>
          <SubscriptionIcon style={{ width: 20, marginRight: 10 }} />
          {planSubscribe?.planName}
        </h3>
        <Divider />
        <Row gutter={ROW_GUTTER}>
          <Col md={12}>
            <Card className="mtopo-card" size="small" title={t('Additional Services')}>
              {planSubscribe?.services?.length ? (
                <ul style={{ listStyle: 'inside' }}>
                  {planSubscribe?.services?.map(service => {
                    return <li>{service.serviceName}</li>;
                  })}
                </ul>
              ) : (
                <div style={{ color: '#cf1322', textAlign: 'center' }}>{t('subscriptionsPage_NoData')}</div>
              )}
            </Card>
          </Col>

          <Col md={12}>
            <Card title={t('Payment Details')} className="mtopo-card" size="small">
              {planSubscribe?.hidePaySection ? (
                <div style={{ color: '#cf1322', textAlign: 'center' }}>{t('subscriptionsPage_NotAvailable')}</div>
              ) : (
                <div style={{ textAlign: 'end' }}>
                  <span style={{ display: 'flex', justifyContent: 'end' }}>
                    <span>{t('subscriptionsPage_PlanMonthlyFees')}</span>
                    <b style={{ minWidth: 100 }}>{formatCurrency(planSubscribe?.price)}</b>
                  </span>
                  <span style={{ display: 'flex', justifyContent: 'end' }}>
                    <span> {t('subscriptionsPage_PlanComponentFees')}</span>
                    <b style={{ minWidth: 100 }}>{formatCurrency(planSubscribe?.existingComponentsFees)}</b>
                  </span>
                  <span style={{ display: 'flex', justifyContent: 'end' }}>
                    <span> {t('subscriptionsPage_TotalAmount')}</span>
                    <b style={{ minWidth: 100 }}>{formatCurrency((planSubscribe?.price || 0) + (planSubscribe?.existingComponentsFees || 0))}</b>
                  </span>
                  <Divider />
                  <Button
                    type="primary"
                    onClick={() => {
                      subscribeNow();
                    }}
                  >
                    {t('subscriptionsPage_SubscribeNow')}
                  </Button>
                </div>
              )}
            </Card>
          </Col>
          <Col md={24}>
            <Card className="mtopo-card" size="small" title={t('Note')}>
              <b>{t('subscriptionsPage_Note')} :</b> {t('subscriptionsPage_NoteDetail')}
              <br /> <br />
              {planSubscribe?.hidePaySection ? (
                <div>{t('subscriptionsPage_FreePlanNote')}</div>
              ) : (
                <div>
                  {t('subscriptionsPage_PlanExpiresOn')}
                  <b>{formatDisplayDate(moment().add(30, 'day'))}</b>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
}
